import React, { memo, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, { runOnJS, useDerivedValue, useAnimatedStyle, useAnimatedProps, } from 'react-native-reanimated';
import { rem, Spacer } from 'design-system';
import Text, { styles as textStyles } from 'design-system/Text';
import { AnimatedTextInput } from 'design-system/AnimatedComponents';
import i18n from 'utils/i18n';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { selectAssetBreakdownSections } from 'reducers/selectors';
import { isWeb } from '../../../../constants';
const gap = rem(6);
// The tooltip's free side. 'auto' on every platform: Reanimated 4 keeps a
// value it set earlier when the next style leaves it undefined, so the box
// stayed pinned on both sides after the cursor crossed the middle.
const defaultLeftAndRight = 'auto';
const NetworthAssetBreakdownView = ({ width, selectedInfoObj }) => {
    const styles = useStyles(styleSet);
    const sections = useAppSelector(selectAssetBreakdownSections);
    const animatedStyles = useAnimatedStyle(() => {
        if (!width || !selectedInfoObj.value || !selectedInfoObj.value.breakdown) {
            return {
                opacity: 0,
            };
        }
        const { x } = selectedInfoObj.value;
        if (x > 0.49 * width) {
            return {
                opacity: 1,
                right: width - x + gap,
                left: defaultLeftAndRight,
            };
        }
        return {
            left: x + gap,
            opacity: 1,
            right: defaultLeftAndRight,
        };
    }, [selectedInfoObj, width]);
    const [webState, setWebState] = useState(undefined);
    useDerivedValue(() => {
        if (isWeb && selectedInfoObj.value) {
            runOnJS(setWebState)(selectedInfoObj.value);
        }
    }, [selectedInfoObj]);
    const savingsProps = useAnimatedProps(() => {
        if (!isWeb && selectedInfoObj.value?.breakdown?.savings) {
            return {
                text: selectedInfoObj.value.breakdown.savings,
            };
        }
        return {};
    }, [selectedInfoObj]);
    const investmentProps = useAnimatedProps(() => {
        if (!isWeb && selectedInfoObj.value?.breakdown?.investment) {
            return {
                text: selectedInfoObj.value.breakdown.investment,
            };
        }
        return {};
    }, [selectedInfoObj]);
    const otherProps = useAnimatedProps(() => {
        if (!isWeb && selectedInfoObj.value?.breakdown?.other) {
            return {
                text: selectedInfoObj.value.breakdown.other,
            };
        }
        return {};
    }, [selectedInfoObj]);
    const currentProps = useAnimatedProps(() => {
        if (!isWeb && selectedInfoObj.value?.breakdown?.current) {
            return {
                text: selectedInfoObj.value.breakdown.current,
            };
        }
        return {};
    }, [selectedInfoObj]);
    const rows = useMemo(() => [
        {
            key: 'current',
            label: i18n.t('current'),
            webValue: webState?.breakdown?.current,
            animatedProps: currentProps,
        },
        {
            key: 'savings',
            label: 'Savings',
            webValue: webState?.breakdown?.savings,
            animatedProps: savingsProps,
        },
        {
            key: 'investment',
            label: 'Investment',
            webValue: webState?.breakdown?.investment,
            animatedProps: investmentProps,
        },
        {
            key: 'other',
            label: 'Other',
            webValue: webState?.breakdown?.other,
            animatedProps: otherProps,
        },
    ].filter((row) => sections[row.key]), [
        currentProps,
        savingsProps,
        investmentProps,
        otherProps,
        sections,
        webState?.breakdown,
    ]);
    const PriceContent = useMemo(() => {
        if (isWeb) {
            return rows.map((row) => (<Text NumbersThin-12 key={row.key}>
          {row.webValue || ' '}
        </Text>));
        }
        return rows.map((row) => (<AnimatedTextInput key={row.key} editable={false} defaultValue={''} numberOfLines={1} allowFontScaling={false} style={styles.priceStyles} animatedProps={row.animatedProps}/>));
    }, [rows, styles.priceStyles]);
    if (rows.length < 2) {
        return null;
    }
    return (<Animated.View style={styles.container} pointerEvents="none">
      <Animated.View style={[styles.flex, animatedStyles]}>
        <View style={styles.infoContainer}>
          <View>
            {rows.map((row, index) => (<React.Fragment key={row.key}>
                {index > 0 ? <Spacer h={6}/> : null}
                <Text TextThin-12 Secondary>
                  {row.label}:
                </Text>
              </React.Fragment>))}
          </View>
          <Spacer w={8}/>
          <View style={styles.priceContainer}>{PriceContent}</View>
        </View>
      </Animated.View>
    </Animated.View>);
};
export default memo(NetworthAssetBreakdownView);
const styleSet = createStyleSheets((colors) => ({
    container: {
        ...StyleSheet.absoluteFillObject,
        flexDirection: 'row',
    },
    infoContainer: {
        padding: rem(12),
        borderRadius: rem(8),
        flexDirection: 'row',
        backgroundColor: colors.background.light,
        ...colors.shadows.cardLarge,
    },
    priceContainer: {
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    flex: {
        top: 0,
        bottom: 0,
        position: 'absolute',
        flexDirection: 'row',
        alignItems: 'center',
    },
    priceStyles: {
        ...textStyles.NumbersThin_12,
        margin: 0,
        padding: 0,
        height: rem(18),
        color: colors.text.primary,
    },
}));
